
import React from 'react';
import { SampleRequest } from '../../types';
import { ChevronRight, Clock, Zap, Shirt } from 'lucide-react';
import { clsx } from 'clsx';
import { getDerivedStatus } from '../../domain';

interface Props {
  sample: SampleRequest;
  onClick: () => void;
}

const statusConfig: Record<string, { bg: string; text: string; dot: string }> = {
  'File Received': { bg: 'bg-slate-50', text: 'text-slate-500', dot: 'bg-slate-400' },
  'File Handover': { bg: 'bg-slate-50', text: 'text-slate-500', dot: 'bg-slate-400' },
  'Cutting': { bg: 'bg-amber-50', text: 'text-amber-600', dot: 'bg-amber-500' },
  'Sewing': { bg: 'bg-violet-50', text: 'text-violet-600', dot: 'bg-violet-500' },
  'Inline QC': { bg: 'bg-orange-50', text: 'text-orange-600', dot: 'bg-orange-500' },
  'Final QC': { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500' },
  'Rework': { bg: 'bg-red-50', text: 'text-red-500', dot: 'bg-red-500' },
  'Completed': { bg: 'bg-green-50', text: 'text-green-600', dot: 'bg-green-500' },
};

const getStatusStyle = (status: string) => statusConfig[status] || statusConfig['File Handover'];

export const SampleRequestCard: React.FC<Props> = ({ sample, onClick }) => {
  const today = new Date();
  const deadline = new Date(sample.deadline_date);
  const diffDays = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const isPastDue = diffDays < 0;

  const derivedStatus = getDerivedStatus(sample);
  const isCompleted = derivedStatus === 'Completed';
  const sty = getStatusStyle(derivedStatus);
  
  // Get sewing dates from sample_planning
  const plan = sample.sample_planning;
  const sewingStart = plan?.sewing_start_at;
  const sewingEnd = plan?.sewing_end_at;

  return (
    <div
      onClick={onClick}
      className={clsx(
        "bg-blue-50/50 rounded-2xl p-4 border border-blue-100 relative overflow-hidden",
        "active:scale-[0.98] transition-all shadow-sm shadow-blue-100/20"
      )}
    >
      <div className="flex justify-between items-start mb-3 relative z-10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white border border-blue-100 overflow-hidden flex-shrink-0 shadow-sm">
            {sample.styles?.image_url ? (
              <img src={sample.styles.image_url} alt={sample.styles.style_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs font-bold text-blue-600">
                {sample.styles?.style_code?.slice(-2) || 'S'}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-800 text-sm truncate">{sample.styles?.style_name || sample.styles?.style_code}</h3>
            <p className="text-[10px] uppercase font-bold text-blue-400 tracking-wide">{sample.styles?.buyer_name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[9px] text-slate-500 font-medium">{sample.styles?.category}</span>
              <span className="text-[9px] text-slate-300">•</span>
              <span className="text-[9px] text-blue-600 font-bold">{sample.sample_type} V{sample.version_number}</span>
            </div>
          </div>
        </div>

        <div className={clsx("px-2.5 py-1 rounded-lg flex items-center gap-1.5 border", sty.bg, isCompleted ? "border-emerald-100" : "border-slate-100")}>
          <span className={clsx("w-1.5 h-1.5 rounded-full animate-pulse", sty.dot)}></span>
          <span className={clsx("text-[10px] font-bold uppercase tracking-wide", sty.text)}>{derivedStatus}</span>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs font-medium text-slate-500 relative z-10 pl-1">
        <div className="flex items-center gap-4">
          {sample.is_fasttrack && (
            <div className="flex items-center gap-1 text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-100">
              <Zap size={10} fill="currentColor" />
              <span className="text-[9px] font-bold">FAST</span>
            </div>
          )}
          <div className={clsx("flex items-center gap-1.5", isPastDue ? "text-red-500 font-bold" : "")}>
            <Clock size={12} />
            <span>{new Date(sample.deadline_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
          </div>
        </div>

        <div className="flex items-center gap-1 text-blue-400 bg-white px-2 py-1 rounded-lg border border-blue-50">
          <span className="font-bold">{sample.requested_qty}</span>
          <span className="text-[9px] uppercase">Pcs</span>
          <ChevronRight size={12} />
        </div>
      </div>
    </div>
  );
};